import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { Dexie } from 'dexie';
import { useLiveQuery } from 'dexie-react-hooks';
import { FlipHorizontal2, ImagePlus, RotateCw, Undo2, ZoomIn, ZoomOut } from 'lucide-react';
import { useDropzone } from 'react-dropzone';

import { apiPost } from '../../lib/api_v2';
import db from '../../lib/db';

import { logError } from '../../lib/logger';
import {
  MediaAttachmentStates,
  enqueueUpload,
  removeMediaAttachment,
  removeMediaAttachmentsForSessionKey,
  useMediaAttachment,
} from '../../lib/media_attachments_store';
import { cn } from '../../lib/utils';

import { EMOJI_CONTENT_TYPES, deriveShortcodeFromFilename, normalizeShortcode } from '../../lib/customEmoji';

import { useRouteWorkspace } from '../../contexts/RouteWorkspaceContext';
import {
  SETTINGS_MODAL_CLOSE_BUTTON_CLASS,
  SETTINGS_MODAL_CLOSE_ICON,
  SETTINGS_MODAL_SAVE_PILL_CLASS,
  SETTINGS_MODAL_TITLE_CLASS,
} from '../shared/settingsModalChrome';
import { Tooltip } from '../shared/Tooltip';
import { Modal } from './Modal';
import { ModalBody } from './ModalBody';
import { ModalContent } from './ModalContent';
import { ModalTopBar } from './ModalTopBar';

const UPLOAD_SESSION_KEY = 'emoji-uploader:add-emoji-modal';
const UPLOAD_CHANNEL_ID = 'modal:add-emoji';
// On-screen crop viewport, in CSS px. The export canvas renders at 2x for retina-crisp
// 160px pipeline variants.
const VIEWPORT = 320;
const EXPORT_SIZE = 640;
const MIN_ZOOM = 1;
const MAX_ZOOM = 3;
// The pipeline re-encodes everything down to 160px variants, so the cap is about
// upload time and canvas memory, not storage.
const MAX_FILE_BYTES = 10 * 1024 * 1024;

// Checkerboard that signals transparency inside the crop box, like every image editor.
const CHECKERBOARD_CLASS =
  'bg-[repeating-conic-gradient(#e5e7eb_0%_25%,#f9fafb_0%_50%)] bg-[size:16px_16px] dark:bg-[repeating-conic-gradient(#26262a_0%_25%,#1c1c1f_0%_50%)]';

// Displayed image dims after rotation (90/270 swap the axes).
const rotatedDims = (width, height, rotation) => (rotation % 180 === 0 ? { rw: width, rh: height } : { rw: height, rh: width });

const AddEmojiModal = ({ onClose, onSubmittingChange }) => {
  const { workspaceId } = useRouteWorkspace();

  const [file, setFile] = useState(null);
  const [imageUrl, setImageUrl] = useState(null);
  const [naturalSize, setNaturalSize] = useState(null); // { width, height }
  const [zoom, setZoom] = useState(MIN_ZOOM);
  const [offset, setOffset] = useState({ x: 0, y: 0 });
  const [rotation, setRotation] = useState(0);
  const [flipH, setFlipH] = useState(false);
  const [shortcode, setShortcode] = useState('');
  const [error, setError] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [uploadId, setUploadId] = useState(null);

  const imageElRef = useRef(null);
  const dragStateRef = useRef(null);
  const submittedRef = useRef(false);

  const isGif = file?.type === 'image/gif';
  const upload = useMediaAttachment(uploadId);

  // Contain scale: at zoom 1 the whole image fits inside the crop box (rectangular
  // images keep their shape and export with transparent padding); zoom multiplies on top.
  const scale = useMemo(() => {
    if (!naturalSize) return 1;
    const { rw, rh } = rotatedDims(naturalSize.width, naturalSize.height, rotation);
    return Math.min(VIEWPORT / rw, VIEWPORT / rh) * zoom;
  }, [naturalSize, rotation, zoom]);

  const clampOffset = useCallback(
    (next, nextScale = scale) => {
      if (!naturalSize) return { x: 0, y: 0 };
      const { rw, rh } = rotatedDims(naturalSize.width, naturalSize.height, rotation);
      const maxX = Math.max(0, (rw * nextScale - VIEWPORT) / 2);
      const maxY = Math.max(0, (rh * nextScale - VIEWPORT) / 2);
      return { x: Math.min(maxX, Math.max(-maxX, next.x)), y: Math.min(maxY, Math.max(-maxY, next.y)) };
    },
    [naturalSize, rotation, scale],
  );

  const resetTransform = useCallback(() => {
    setZoom(MIN_ZOOM);
    setOffset({ x: 0, y: 0 });
    setRotation(0);
    setFlipH(false);
  }, []);

  const cleanupUpload = useCallback(() => {
    removeMediaAttachmentsForSessionKey(UPLOAD_SESSION_KEY).catch(() => {});
  }, []);

  // The X is the always-available exit: it closes even mid-upload, so no upload state can
  // strand the user. Esc and outside-press stay gated by the host against stray dismissals.
  const handleCloseButton = useCallback(() => onClose({ force: true }), [onClose]);

  // The host gates dismissal on this so the modal can't close mid-upload.
  useEffect(() => {
    onSubmittingChange?.(isSubmitting);
    return () => onSubmittingChange?.(false);
  }, [isSubmitting, onSubmittingChange]);

  // Object URL lifecycle + stale-upload sweep on mount/unmount.
  useEffect(() => {
    cleanupUpload();
    return cleanupUpload;
  }, [cleanupUpload]);
  useEffect(() => {
    if (!imageUrl) return undefined;
    return () => URL.revokeObjectURL(imageUrl);
  }, [imageUrl]);

  const handleDropRejected = useCallback((rejections) => {
    const tooLarge = rejections?.some((rejection) => rejection?.errors?.some((err) => err?.code === 'file-too-large'));
    setError(tooLarge ? 'Images must be under 10MB.' : 'That file type is not supported.');
  }, []);

  const handleDrop = useCallback((acceptedFiles) => {
    const dropped = acceptedFiles[0];
    if (!dropped) return;
    if (dropped.size > MAX_FILE_BYTES) {
      setError('Images must be under 10MB.');
      return;
    }
    setFile(dropped);
    setImageUrl(URL.createObjectURL(dropped));
    setNaturalSize(null);
    setError(null);
    setZoom(MIN_ZOOM);
    setOffset({ x: 0, y: 0 });
    setRotation(0);
    setFlipH(false);
    setShortcode((current) => current || deriveShortcodeFromFilename(dropped.name));
  }, []);

  const dropzoneAccept = useMemo(
    () =>
      EMOJI_CONTENT_TYPES.reduce((acc, type) => {
        acc[type] = [];
        return acc;
      }, {}),
    [],
  );

  const { getRootProps, getInputProps, open: openFilePicker, isDragActive } = useDropzone({
    // Clicks are handled explicitly (blank-state target + Browse button) so they can't double-fire.
    noClick: true,
    multiple: false,
    accept: dropzoneAccept,
    maxSize: MAX_FILE_BYTES,
    onDrop: handleDrop,
    onDropRejected: handleDropRejected,
  });

  const handleImageLoad = useCallback((event) => {
    setNaturalSize({ width: event.target.naturalWidth, height: event.target.naturalHeight });
  }, []);

  // Pointer-drag repositioning, clamped so the image always covers the crop box.
  const handlePointerDown = useCallback(
    (event) => {
      // isSubmitting: the overlay sits inside the crop box, so a press on it still bubbles
      // here — without this you could drag the image out from under its own upload.
      if (!naturalSize || isGif || isSubmitting) return;
      event.currentTarget.setPointerCapture(event.pointerId);
      dragStateRef.current = { startX: event.clientX, startY: event.clientY, base: offset };
    },
    [naturalSize, isGif, isSubmitting, offset],
  );
  const handlePointerMove = useCallback(
    (event) => {
      const drag = dragStateRef.current;
      if (!drag) return;
      setOffset(clampOffset({ x: drag.base.x + (event.clientX - drag.startX), y: drag.base.y + (event.clientY - drag.startY) }));
    },
    [clampOffset],
  );
  const handlePointerUp = useCallback(() => {
    dragStateRef.current = null;
  }, []);

  const handleZoomChange = useCallback(
    (event) => {
      const nextZoom = Number(event.target.value);
      setZoom(nextZoom);
      if (!naturalSize) return;
      const { rw, rh } = rotatedDims(naturalSize.width, naturalSize.height, rotation);
      const nextScale = Math.min(VIEWPORT / rw, VIEWPORT / rh) * nextZoom;
      setOffset((current) => clampOffset(current, nextScale));
    },
    [clampOffset, naturalSize, rotation],
  );

  const handleRotate = useCallback(() => {
    setRotation((current) => (current + 90) % 360);
    setOffset({ x: 0, y: 0 });
  }, []);
  const handleFlip = useCallback(() => setFlipH((current) => !current), []);

  // Rasterize the on-screen crop. GIFs skip cropping entirely (canvas would freeze
  // the first frame), so the original file uploads untouched.
  const exportCroppedFile = useCallback(async () => {
    if (isGif) return file;

    const img = imageElRef.current;
    if (!img || !naturalSize) return null;

    const canvas = document.createElement('canvas');
    canvas.width = EXPORT_SIZE;
    canvas.height = EXPORT_SIZE;
    const ctx = canvas.getContext('2d');
    const k = EXPORT_SIZE / VIEWPORT;

    ctx.translate(EXPORT_SIZE / 2 + offset.x * k, EXPORT_SIZE / 2 + offset.y * k);
    ctx.rotate((rotation * Math.PI) / 180);
    ctx.scale((flipH ? -scale : scale) * k, scale * k);
    ctx.drawImage(img, -naturalSize.width / 2, -naturalSize.height / 2);

    const blob = await new Promise((resolve) => canvas.toBlob(resolve, 'image/png'));
    if (!blob) return null;
    const baseName = normalizeShortcode(shortcode) || 'emoji';
    return new File([blob], `${baseName}.png`, { type: 'image/png' });
  }, [file, flipH, isGif, naturalSize, offset, rotation, scale, shortcode]);

  const handleSubmit = useCallback(
    async (event) => {
      event?.preventDefault();
      if (isSubmitting) return;

      const normalized = normalizeShortcode(shortcode);
      if (!normalized) {
        setError('Please enter a name');
        return;
      }
      if (!file) {
        setError('Please add an image');
        return;
      }

      setError(null);
      setIsSubmitting(true);
      submittedRef.current = false;

      try {
        const uploadFile = await exportCroppedFile();
        if (!uploadFile) throw new Error('crop export failed');
        const nextUploadId = await enqueueUpload({
          file: uploadFile,
          purpose: 'emoji_custom',
          workspaceId,
          sessionKey: UPLOAD_SESSION_KEY,
          channelId: UPLOAD_CHANNEL_ID,
        });
        setUploadId(nextUploadId);
      } catch (submitError) {
        logError('AddEmojiModal', 'Failed to start emoji upload', { error: submitError });
        setError('Failed to start upload. Please try again.');
        setIsSubmitting(false);
      }
    },
    [exportCroppedFile, file, isSubmitting, shortcode, workspaceId],
  );

  // Once the attachment finishes uploading, create the emoji and close.
  useEffect(() => {
    if (!isSubmitting || !uploadId || submittedRef.current) return;

    const state = upload?.upload?.state;
    if (state === MediaAttachmentStates.error) {
      setError('Upload failed. Please try again.');
      setIsSubmitting(false);
      setUploadId(null);
      return;
    }
    if (state !== MediaAttachmentStates.completed || !upload?.attachment_id) return;

    submittedRef.current = true;
    apiPost('/emojis', { workspace_id: workspaceId, emoji: { shortcode: normalizeShortcode(shortcode), custom: upload.attachment_id } })
      .then(async (response) => ({ response, data: await response.json() }))
      .then(({ response, data }) => {
        if (response.ok && data.ok === true) {
          removeMediaAttachment(uploadId).catch(() => {});
          // Release the host's dismissal gate synchronously. It reads a ref that the
          // isSubmitting effect wouldn't write until after this tick, so onClose() would
          // otherwise be swallowed — leaving the modal open, spinning, with a dead X while
          // the just-created emoji syncs in and trips the shortcode-conflict warning.
          setIsSubmitting(false);
          onSubmittingChange?.(false);
          onClose();
        } else {
          const validations = data.validations || {};
          const firstMessage = Object.values(validations)
            .flatMap((message) => (Array.isArray(message) ? message : [message]))
            .find(Boolean);
          setError(firstMessage || 'Failed to create emoji.');
          setIsSubmitting(false);
          setUploadId(null);
        }
      })
      .catch((createError) => {
        logError('AddEmojiModal', 'Failed to create emoji', { error: createError });
        setError('Failed to create emoji. Please try again.');
        setIsSubmitting(false);
        setUploadId(null);
      });
  }, [isSubmitting, onClose, onSubmittingChange, shortcode, upload, uploadId, workspaceId]);

  // Progress belongs to the thing being uploaded, so it's narrated over the crop box
  // rather than on the button. `progress: null` means the stage reports no percentage —
  // the server-side variant pass has none to give, and parking a bar at 100 through it
  // would read as wedged.
  const uploadStatus = useMemo(() => {
    if (!isSubmitting) return null;

    switch (upload?.upload?.state) {
      case MediaAttachmentStates.uploading: {
        const progress = Math.round(upload?.upload?.progress ?? 0);
        return { label: `Uploading ${progress}%`, progress };
      }
      case MediaAttachmentStates.processing:
        return { label: 'Processing', progress: null };
      case MediaAttachmentStates.retrying:
        return { label: 'Retrying', progress: null };
      case MediaAttachmentStates.completed:
        return { label: 'Saving', progress: null };
      default:
        return { label: 'Uploading', progress: 0 };
    }
  }, [isSubmitting, upload]);

  const hasTransform = zoom !== MIN_ZOOM || offset.x !== 0 || offset.y !== 0 || rotation !== 0 || flipH;
  // Once submitted the crop has already been rasterized and sent, so leaving these live
  // would let you adjust an image that no longer matches what's uploading.
  const cropControlsDisabled = !file || isGif || isSubmitting;

  // Bad characters: anything normalization would silently strip (spaces are fine — they become dashes).
  const hasInvalidCharacters = Boolean(shortcode.trim()) && /[^a-zA-Z0-9_\s-]/.test(shortcode.trim());

  // Reactive conflict check against active emojis (system + this workspace's customs),
  // same query the Custom Emojis settings page runs.
  const hasShortcodeConflict = useLiveQuery(
    async () => {
      const normalized = normalizeShortcode(shortcode);
      if (!normalized || !workspaceId) return false;
      const [systemEmojis, workspaceEmojis] = await Promise.all([
        db.emojis.where('[workspace_id+sort_order]').between(['system', 0], ['system', Dexie.maxKey], true, true).toArray(),
        db.emojis.where('[workspace_id+sort_order]').between([workspaceId, 0], [workspaceId, Dexie.maxKey], true, true).toArray(),
      ]);
      return [...systemEmojis, ...workspaceEmojis].some((existing) => existing.shortcode === normalized);
    },
    [shortcode, workspaceId],
  );

  const canSubmit =
    Boolean(file) && Boolean(normalizeShortcode(shortcode)) && !hasInvalidCharacters && !hasShortcodeConflict && !isSubmitting;

  const cropControlButtonClass =
    'flex size-9 shrink-0 cursor-pointer items-center justify-center text-gray-500 hover:text-black dark:text-neutral-400 dark:hover:text-white';

  return (
    <Modal
      onClose={handleCloseButton}
      className="rounded-2xl"
      closePlacement="top-left"
      closeButtonClassName={SETTINGS_MODAL_CLOSE_BUTTON_CLASS}
      closeIcon={SETTINGS_MODAL_CLOSE_ICON}
    >
      <ModalBody className="h-full">
        <ModalTopBar className="!pb-0">
          <span className={SETTINGS_MODAL_TITLE_CLASS}>Add Emoji</span>
        </ModalTopBar>

        <ModalContent className="min-h-0 md:!px-5">
          <div className="flex w-full flex-col pt-6 pb-5">
            {/* Blue action pill top-right, opposite the close button — same spec as every settings
                modal. Nested (not a direct ModalBody child) so the body's child padding can't inflate it;
                the absolute positioning still anchors it to the Modal root. */}
            <button
              type="button"
              data-app-region="none"
              disabled={!canSubmit}
              onClick={handleSubmit}
              className={SETTINGS_MODAL_SAVE_PILL_CLASS}
            >
              Add
            </button>

            {/* items-start keeps the blank square and the crop box top-aligned, so loading an image doesn't shift the layout. */}
            <div {...getRootProps()} className="relative flex min-h-105 flex-col items-center rounded-xl">
              <input {...getInputProps()} />

              {!file && (
                <div
                  onClick={openFilePicker}
                  className={cn(
                    // Same footprint as the crop box so loading an image doesn't reflow the modal.
                    'flex cursor-pointer flex-col items-center justify-center gap-3 rounded-xl border-[0.5px] border-white bg-gray-50/80 text-gray-500 hover:bg-white/80 dark:border-neutral-800 dark:bg-neutral-900/80 dark:text-neutral-400 dark:hover:bg-neutral-800/80',
                    isDragActive && 'bg-white/90 text-black ring-2 ring-idle-blue dark:text-neutral-100',
                  )}
                  style={{ width: VIEWPORT, height: VIEWPORT }}
                >
                  <ImagePlus size={28} strokeWidth={1.5} absoluteStrokeWidth />
                  <span className="text-sm leading-5 font-medium">{isDragActive ? 'Drop to add' : 'Drag an image here'}</span>
                  <button
                    type="button"
                    onClick={(event) => {
                      event.stopPropagation();
                      openFilePicker();
                    }}
                    className="mt-3 flex h-9 shrink-0 cursor-pointer items-center justify-center rounded-full bg-gray-200 px-4 text-sm leading-[18px] font-medium text-black hover:bg-gray-300 dark:bg-neutral-800 dark:text-neutral-100 dark:hover:bg-neutral-700"
                  >
                    Browse files
                  </button>
                  <span className="mt-1 text-[11px] leading-4 font-medium text-gray-400 dark:text-neutral-500">
                    Up to 10MB · PNG, JPG, GIF, WebP
                  </span>
                </div>
              )}

              {file && (
                <>
                  <div
                    className={cn(
                      // The crop box is the only checkered surface — a clearly bounded square
                      // whose checkerboard shows exactly where transparency will end up.
                      'relative overflow-hidden rounded-xl ring-1 ring-black/10 dark:ring-white/10',
                      CHECKERBOARD_CLASS,
                      isGif ? 'flex items-center justify-center' : 'cursor-grab active:cursor-grabbing',
                    )}
                    style={{ width: VIEWPORT, height: VIEWPORT }}
                    onPointerDown={handlePointerDown}
                    onPointerMove={handlePointerMove}
                    onPointerUp={handlePointerUp}
                    onPointerCancel={handlePointerUp}
                  >
                    <img
                      ref={imageElRef}
                      src={imageUrl}
                      alt=""
                      draggable={false}
                      onLoad={handleImageLoad}
                      className="absolute top-1/2 left-1/2 max-w-none select-none"
                      style={
                        isGif
                          ? { maxWidth: VIEWPORT, maxHeight: VIEWPORT, transform: 'translate(-50%, -50%)' }
                          : {
                              transform: `translate(-50%, -50%) translate(${offset.x}px, ${offset.y}px) rotate(${rotation}deg) scale(${flipH ? -scale : scale}, ${scale})`,
                              width: naturalSize?.width,
                              height: naturalSize?.height,
                            }
                      }
                    />

                    {uploadStatus && (
                      // Sits over the crop box so the emoji you just framed is the thing you
                      // watch upload. The image stays legible through the scrim on purpose.
                      <div className="absolute inset-0 z-10 flex cursor-default flex-col items-center justify-center gap-3 bg-black/55 backdrop-blur-[1px]">
                        <span className="text-sm leading-5 font-medium text-white tabular-nums">{uploadStatus.label}</span>
                        <div className="h-1 w-40 overflow-hidden rounded-full bg-white/25">
                          {uploadStatus.progress === null ? (
                            <div className="animate-progress-indeterminate h-full w-1/3 rounded-full bg-white" />
                          ) : (
                            <div
                              className="h-full rounded-full bg-white transition-[width] duration-150 ease-out"
                              style={{ width: `${uploadStatus.progress}%` }}
                            />
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                </>
              )}

              {/* Controls stay put in the empty state (and for GIFs) so nothing jumps — just inert. */}
              <div
                className={cn(
                  'mt-5 mb-1 flex items-center gap-1 rounded-full border-[0.5px] border-white bg-gray-50 px-2 py-1 shadow-[0px_4px_4px_rgba(0,0,0,0.02)] dark:border-white/10 dark:bg-neutral-900',
                  cropControlsDisabled && 'pointer-events-none opacity-40',
                )}
              >
                <Tooltip label="Rotate">
                  <button
                    type="button"
                    onClick={handleRotate}
                    disabled={cropControlsDisabled}
                    aria-label="Rotate image"
                    className={cropControlButtonClass}
                  >
                    <RotateCw size={16} strokeWidth={1.5} absoluteStrokeWidth />
                  </button>
                </Tooltip>
                <Tooltip label="Flip">
                  <button
                    type="button"
                    onClick={handleFlip}
                    disabled={cropControlsDisabled}
                    aria-label="Flip image"
                    className={cropControlButtonClass}
                  >
                    <FlipHorizontal2 size={16} strokeWidth={1.5} absoluteStrokeWidth />
                  </button>
                </Tooltip>
                <Tooltip label="Reset">
                  <button
                    type="button"
                    onClick={resetTransform}
                    disabled={cropControlsDisabled || !hasTransform}
                    aria-label="Reset image adjustments"
                    className={cn(cropControlButtonClass, 'disabled:cursor-not-allowed disabled:opacity-40')}
                  >
                    <Undo2 size={16} strokeWidth={1.5} absoluteStrokeWidth />
                  </button>
                </Tooltip>
                <div className="mx-1 h-5 w-px bg-gray-200 dark:bg-neutral-700" />
                <ZoomOut size={16} strokeWidth={1.5} absoluteStrokeWidth className="shrink-0 text-gray-500 dark:text-neutral-400" />
                <input
                  type="range"
                  min={MIN_ZOOM}
                  max={MAX_ZOOM}
                  step={0.01}
                  value={zoom}
                  onChange={handleZoomChange}
                  disabled={cropControlsDisabled}
                  aria-label="Zoom"
                  className="mx-2 w-56 accent-black dark:accent-white"
                />
                <ZoomIn size={16} strokeWidth={1.5} absoluteStrokeWidth className="shrink-0 text-gray-500 dark:text-neutral-400" />
              </div>
            </div>

            <form onSubmit={handleSubmit} className="-mt-4">
              <label htmlFor="add-emoji-shortcode" className="block text-cursed text-black dark:text-neutral-100">
                New Emoji
              </label>
              {/* The colons are permanent bookends, so the field reads :name: as you type. */}
              <div className="mt-2 flex h-12 w-full items-center rounded-lg border sm:border-[0.5px] border-white bg-gray-50 px-4 text-[15px] leading-5 hover:bg-white focus-within:bg-white dark:border-neutral-800 dark:bg-neutral-900">
                <span className="shrink-0 text-gray-400 dark:text-neutral-500">:</span>
                <input
                  type="text"
                  id="add-emoji-shortcode"
                  value={shortcode}
                  onChange={(event) => setShortcode(event.target.value)}
                  placeholder="name"
                  autoComplete="off"
                  data-1p-ignore="true"
                  spellCheck={false}
                  aria-label="Emoji name"
                  className="max-w-full min-w-4 bg-transparent text-black outline-none [field-sizing:content] placeholder:text-gray-400 dark:text-neutral-100 dark:placeholder:text-neutral-500"
                />
                <span className="shrink-0 text-gray-400 dark:text-neutral-500">:</span>
              </div>
            </form>
            {hasInvalidCharacters && (
              <span className="mt-2 block text-left text-xs text-red-400">
                Names can only use letters, numbers, dashes, and underscores.
              </span>
            )}
            {!hasInvalidCharacters && hasShortcodeConflict && (
              <span className="mt-2 block text-left text-xs text-red-400">Name conflicts with an existing emoji.</span>
            )}
            {error && <span className="mt-2 block text-left text-xs text-red-400">{error}</span>}
          </div>
        </ModalContent>
      </ModalBody>
    </Modal>
  );
};

export default AddEmojiModal;
